import { faFacebookF, faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { faComment, faPhone } from "@fortawesome/pro-regular-svg-icons";
import { faDesktop, faEnvelope, faMobile } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useMemo } from "react";
import { Popup } from "semantic-ui-react";

export function ChannelIconList({ channels }: { channels: ChannelIconType[] }) {
  // Sort channel order should be -> Instagram, Facebook, Twitter, Email, Sms, Voice, Push, Web
  const sortedChannels = useMemo(() => {
    const channelOrder: ChannelIconType[] = [
      "Instagram",
      "Facebook",
      "Twitter",
      "Email",
      "Sms",
      "Voice",
      "Push",
      "Web",
    ];
    return channels.sort((a, b) => channelOrder.indexOf(a) - channelOrder.indexOf(b));
  }, [channels]);

  return (
    <div className='tw-flex -tw-space-x-[2px]'>
      {sortedChannels.map((channel, index) => (
        <ChannelIcon key={index} channel={channel} />
      ))}
    </div>
  );
}

export const ChannelIconType = {
  Instagram: "Instagram",
  Facebook: "Facebook",
  Twitter: "Twitter",
  Email: "Email",
  Sms: "Sms",
  Voice: "Voice",
  Push: "Push",
  Web: "Web",
} as const;
export type ChannelIconType = (typeof ChannelIconType)[keyof typeof ChannelIconType];

export function ChannelIcon({
  channel,
  isStaticIcon,
  className,
}: {
  channel: ChannelIconType;
  isStaticIcon?: boolean;
  className?: string;
}) {
  const icon = useMemo(() => {
    const iconTable = {
      [ChannelIconType.Instagram]: faInstagram,
      [ChannelIconType.Facebook]: faFacebookF,
      [ChannelIconType.Twitter]: faXTwitter,
      [ChannelIconType.Email]: faEnvelope,
      [ChannelIconType.Sms]: faComment,
      [ChannelIconType.Voice]: faPhone,
      [ChannelIconType.Push]: faMobile,
      [ChannelIconType.Web]: faDesktop,
    };
    return iconTable[channel];
  }, [channel]);

  const bgColor = useMemo(() => {
    const bgColorTable = {
      [ChannelIconType.Instagram]: "tw-bg-gradient-to-b tw-from-[#F9CE34] tw-via-[#EE2A7B] tw-to-[#6228D7CC]",
      [ChannelIconType.Facebook]: "tw-bg-[#1877F2]",
      [ChannelIconType.Twitter]: "tw-bg-[#14171A]",
      [ChannelIconType.Email]: "tw-bg-[#F2711E]",
      [ChannelIconType.Sms]: "tw-bg-[#5DC959]",
      [ChannelIconType.Voice]: "tw-bg-[#5DC959]",
      [ChannelIconType.Push]: "tw-bg-[#5DC959]",
      [ChannelIconType.Web]: "tw-bg-[#09B4AC]",
    };
    return bgColorTable[channel];
  }, [channel]);

  const tooltip = useMemo(() => {
    const tooltipTable = {
      [ChannelIconType.Instagram]: "Instagram",
      [ChannelIconType.Facebook]: "Facebook",
      [ChannelIconType.Twitter]: "Twitter",
      [ChannelIconType.Email]: "Email",
      [ChannelIconType.Sms]: "Text Message",
      [ChannelIconType.Voice]: "Phone Call",
      [ChannelIconType.Push]: "Push Notification",
      [ChannelIconType.Web]: "Web Notification",
    };
    return tooltipTable[channel];
  }, [channel]);

  const size = useMemo(() => {
    const sizeTable = {
      [ChannelIconType.Instagram]: "",
      [ChannelIconType.Facebook]: "",
      [ChannelIconType.Twitter]: "",
      [ChannelIconType.Email]: "tw-w-[12px] tw-h-[12px]",
      [ChannelIconType.Sms]: "tw-w-[12px] tw-h-[12px]",
      [ChannelIconType.Voice]: "tw-w-[12px] tw-h-[12px]",
      [ChannelIconType.Push]: "tw-w-[12px] tw-h-[12px]",
      [ChannelIconType.Web]: "tw-w-[12px] tw-h-[12px]",
    };
    return sizeTable[channel];
  }, [channel]);

  return (
    <Popup
      trigger={
        <div
          className={classNames(
            "tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-text-white",
            bgColor,
            {
              "tw-ring-1 tw-ring-white": !isStaticIcon,
            },
            className,
          )}
        >
          <FontAwesomeIcon icon={icon} className={size} title={channel} />
        </div>
      }
      inverted
      className='-tw-ml-2'
      disabled={isStaticIcon}
    >
      {tooltip}
    </Popup>
  );
}
